import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const DashboardHeader = ({ onDateRangeChange, onTenantFilterChange, onRefreshIntervalChange }) => {
  const [dateRange, setDateRange] = useState('7d');
  const [tenantFilter, setTenantFilter] = useState('all');
  const [refreshInterval, setRefreshInterval] = useState('5m');
  const [isLive, setIsLive] = useState(true);

  const dateRangeOptions = [
    { value: '24h', label: 'Last 24 Hours' },
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
    { value: '90d', label: 'Last 90 Days' },
    { value: '12m', label: 'Last 12 Months' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const tenantFilterOptions = [
    { value: 'all', label: 'All Tenants' },
    { value: 'enterprise', label: 'Enterprise' },
    { value: 'professional', label: 'Professional' },
    { value: 'starter', label: 'Starter' },
    { value: 'trial', label: 'Trial' }
  ];

  const refreshIntervalOptions = [
    { value: '5m', label: '5 minutes' },
    { value: '15m', label: '15 minutes' },
    { value: '30m', label: '30 minutes' },
    { value: 'manual', label: 'Manual' }
  ];

  const handleDateRangeChange = (value) => {
    setDateRange(value);
    onDateRangeChange?.(value);
  };

  const handleTenantFilterChange = (value) => {
    setTenantFilter(value);
    onTenantFilterChange?.(value);
  };

  const handleRefreshIntervalChange = (value) => {
    setRefreshInterval(value);
    setIsLive(value !== 'manual');
    onRefreshIntervalChange?.(value);
  };

  const handleManualRefresh = () => {
    // Trigger manual refresh
    window.location.reload();
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-1 mb-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
        <div className="flex items-center space-x-4">
          <div>
            <h1 className="text-2xl font-bold text-text-primary">Platform Overview</h1>
            <p className="text-sm text-text-secondary mt-1">
              Comprehensive business intelligence and system monitoring
            </p>
          </div>
          
          <div className="flex items-center space-x-2">
            <div className={`w-3 h-3 rounded-full ${isLive ? 'bg-success animate-pulse' : 'bg-text-secondary'}`}></div>
            <span className="text-xs text-text-secondary">
              {isLive ? 'Live Data' : 'Manual Refresh'}
            </span>
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
          <div className="flex items-center space-x-3">
            <div className="min-w-0">
              <label className="block text-xs font-medium text-text-secondary mb-1">
                Date Range
              </label>
              <Select
                options={dateRangeOptions}
                value={dateRange}
                onChange={handleDateRangeChange}
                className="w-40"
              />
            </div>
            
            <div className="min-w-0">
              <label className="block text-xs font-medium text-text-secondary mb-1">
                Tenant Filter
              </label>
              <Select
                options={tenantFilterOptions}
                value={tenantFilter}
                onChange={handleTenantFilterChange}
                className="w-36"
              />
            </div>
            
            <div className="min-w-0">
              <label className="block text-xs font-medium text-text-secondary mb-1">
                Refresh
              </label>
              <Select
                options={refreshIntervalOptions}
                value={refreshInterval}
                onChange={handleRefreshIntervalChange}
                className="w-32"
              />
            </div>
          </div>
          
          <div className="flex items-center space-x-2 pt-5">
            <Button
              variant="outline"
              size="sm"
              onClick={handleManualRefresh}
              disabled={isLive}
            >
              <Icon name="RefreshCw" size={16} className="mr-2" />
              Refresh
            </Button>
            
            <Button variant="ghost" size="sm">
              <Icon name="Settings" size={16} />
            </Button>
            
            <Button variant="ghost" size="sm">
              <Icon name="Download" size={16} />
            </Button>
          </div>
        </div>
      </div>
      
      <div className="mt-4 pt-4 border-t border-border">
        <div className="flex items-center justify-between text-xs text-text-secondary">
          <div className="flex items-center space-x-4">
            <span>Last updated: {new Date().toLocaleTimeString()}</span>
            <span>•</span>
            <span>Data source: Production Database</span>
            <span>•</span>
            <span>Timezone: {Intl.DateTimeFormat().resolvedOptions().timeZone}</span>
          </div>
          
          <div className="flex items-center space-x-2">
            <Icon name="Shield" size={14} className="text-success" />
            <span>Secure Connection</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardHeader;